/*
    Copyright 2022-2026, Techwise Integrated Security, LLC
*/

import React, { useEffect, useState } from "react";

import { Box, Fade, IconButton, Modal, Stack, Step, StepLabel, Stepper, Typography, useTheme } from "@mui/material";

import Backdrop from "@mui/material/Backdrop";
import {
    Effect,
    InventoryItem,
    InventoryItemWithAvailable,
    ModalBase,
    Nullable,
    StorageLocation,
    TechnicianEntryEgressInventory,
} from "../../utils/Interfaces";

import { Close } from "@mui/icons-material";
import { useAppDispatch } from "../../app/hooks";
import { AnyAction, Dispatch } from "@reduxjs/toolkit";
import { getMyInventories } from "./technicianWorkOrderFunctions";
import { delayedAction, getInventoriesInStorageLocation, listStorageLocationsFromInventories } from "../../utils/Functions";

import { useWindowDimensions } from "../../utils/Hooks";
import InventorySelector from "../../components/modal/transfers/InventorySelector";
import AmountSelectorWithNotes from "../../components/modal/transfers/AmountSelectorWithNotes";
import StorageLocationSelector from "../../components/modal/transfers/StorageLocationSelector";

export interface InventoryEgressModalProps extends ModalBase {
    onTechnicianEgressAdded: Effect;
    existingTechnicianEntryEgresses: TechnicianEntryEgressInventory[];
    onSubmitEgressRequest: (inventoryId: number, amount: number, notes: Nullable<string>, modalCallback: Effect) => void;
}

function InventoryEgressModal(props: InventoryEgressModalProps) {
    const { open, handleClose, onTechnicianEgressAdded, existingTechnicianEntryEgresses, onSubmitEgressRequest } = props;

    const dispatch: Dispatch<AnyAction> = useAppDispatch();

    const { width } = useWindowDimensions();
    const mobile = width <= 600;

    const [activeStep, setActiveStep] = useState<number>(0);

    const [selectedStorageLocation, setSelectedStorageLocation] = useState<Nullable<StorageLocation>>(null);
    const [selectedInventory, setSelectedInventory] = useState<Nullable<InventoryItem>>(null);
    const [amount, setAmount] = useState<number>(0);
    const [notes, setNotes] = useState<string>("");
    const [submitting, setSubmitting] = useState<boolean>(false);

    const [inventories, setInventories] = useState<InventoryItem[]>([]);

    function onClose() {
        handleClose();
        void delayedAction(500, () => {
            setSelectedInventory(null);
            setSelectedStorageLocation(null);
            setAmount(0);
            setActiveStep(0);
            setSubmitting(false);
            setNotes("");
        });
    }

    useEffect(() => {
        getMyInventories(dispatch, setInventories);
    }, [open]);

    const theme = useTheme();

    return (
        <Box>
            <Modal
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
                open={open}
                onClose={onClose}
                closeAfterTransition
                BackdropComponent={Backdrop}
                BackdropProps={{
                    timeout: 500,
                }}
            >
                <Fade in={open}>
                    <Box
                        sx={{
                            backgroundColor: theme.palette.background.paper,
                            border: "1px solid #000",
                            boxShadow: theme.shadows[5],
                            padding: theme.spacing(2, 4, 3),
                            zIndex: 1000,
                            minWidth: mobile ? "100vw" : "90vw",
                        }}
                    >
                        <Stack direction="row" justifyContent="space-between">
                            <Typography variant="h5" sx={{ mb: 3 }}>
                                Add Parts/Materials from Inventory
                            </Typography>
                            <Stack direction="column" justifyContent="flex-start">
                                <IconButton sx={{ mt: -1, mr: -3 }} onClick={onClose}>
                                    <Close fontSize="large" />
                                </IconButton>
                            </Stack>
                        </Stack>

                        <Stepper activeStep={activeStep}>
                            <Step>
                                <StepLabel>Inventory</StepLabel>
                            </Step>
                            <Step>
                                <StepLabel>Material</StepLabel>
                            </Step>
                            <Step>
                                <StepLabel>Amount</StepLabel>
                            </Step>
                        </Stepper>

                        {activeStep === 0 && (
                            <StorageLocationSelector
                                storageLocations={listStorageLocationsFromInventories(inventories)}
                                setSelectedStorageLocation={setSelectedStorageLocation}
                                progressDialog={() => setActiveStep(1)}
                            />
                        )}

                        {activeStep === 1 && (
                            <InventorySelector
                                inventories={getInventoriesInStorageLocation(
                                    selectedStorageLocation,
                                    existingTechnicianEntryEgresses,
                                    inventories
                                )}
                                setSelectedInventory={setSelectedInventory}
                                progressDialog={() => setActiveStep(2)}
                                regressDialog={() => {
                                    setActiveStep(0);
                                    setSelectedStorageLocation(null);
                                }}
                            />
                        )}

                        {activeStep === 2 && (
                            <AmountSelectorWithNotes
                                inventory={selectedInventory as InventoryItemWithAvailable}
                                submitLabel="Add Materials"
                                amount={amount}
                                setAmount={setAmount}
                                notes={notes}
                                setNotes={setNotes}
                                submitting={submitting}
                                progressDialog={() => {
                                    setSubmitting(true);
                                    onSubmitEgressRequest(selectedInventory?.id ?? -1, amount, notes, () => {
                                        onTechnicianEgressAdded();
                                        onClose();
                                    });
                                }}
                                regressDialog={() => {
                                    setActiveStep(1);
                                    setSelectedInventory(null);
                                }}
                            />
                        )}
                    </Box>
                </Fade>
            </Modal>
        </Box>
    );
}

export default InventoryEgressModal;
